import React from 'react';
import PropTypes from 'prop-types';
import { ValuesCache } from '@leverege/value-cache';
import Theme from "./Theme.js";
import Popup from "./Popup.js";
import TextInput from "./TextInput.js";
import NumericInput from "./NumericInput.js";
import Icon from "./IconUtil.js";
import S from './MultiSelector.css';
import Button from "./Button.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default class MultiSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterTerm: null,
      opened: false
    };
    this.inputRef = /*#__PURE__*/React.createRef();
    this.cache = new ValuesCache();
    const {
      valueKey,
      value
    } = props;
    this.valuesMap = props.values?.reduce((prev, curr) => {
      prev[`${curr[valueKey]}`] = curr;
      return prev;
    }, {}) || {};
    this.valueNames = this.getValueNames(value);
  }
  static contextType = Theme.Context;
  static defaultProps = {
    value: [],
    values: [],
    targetAttachment: 'bottom right',
    attachment: 'top right',
    showFilter: false,
    filterOptions: {
      placeholder: 'Search . . .',
      noResults: 'No Results'
    },
    eventData: null,
    nameKey: 'name',
    valueKey: 'value',
    selectionType: undefined,
    allowCreate: false,
    title: null,
    toggleAttributes: undefined,
    showSelectAll: true,
    showFilterSelectAll: true,
    type: 'string',
    float: false,
    disabled: undefined,
    clearSearchOnSelect: false
  };
  static propTypes = {
    values: PropTypes.array,
    value: PropTypes.array,
    eventData: PropTypes.any,
    // eslint-disable-line
    targetAttachment: PropTypes.string,
    attachment: PropTypes.string,
    showFilter: PropTypes.bool,
    filterOptions: PropTypes.object,
    nameKey: PropTypes.string,
    valueKey: PropTypes.string,
    selectionType: PropTypes.oneOf(['text', 'tags']),
    allowCreate: PropTypes.bool,
    toggleVariant: PropTypes.string,
    popupVariant: PropTypes.string,
    title: PropTypes.node,
    icon: PropTypes.node,
    toggleAttributes: PropTypes.object,
    showSelectAll: PropTypes.bool,
    showFilterSelectAll: PropTypes.bool,
    type: PropTypes.oneOf(['string', 'number']),
    float: PropTypes.bool,
    disabled: PropTypes.bool,
    clearSearchOnSelect: PropTypes.bool
  };
  get allSelected() {
    const {
      values = [],
      value,
      valueKey
    } = this.props;
    return this.cache.calc('allSelected', (values, value, valueKey) => {
      return value?.length > 0 && values.every(v => {
        return value?.find(val => {
          return v[valueKey] === val;
        }) != null;
      });
    }, values, value, valueKey);
  }
  get allFilteredSelected() {
    const {
      values = [],
      value,
      valueKey,
      nameKey
    } = this.props;
    const {
      filterTerm
    } = this.state;
    return this.cache.calc('allFilteredSelected', (values, value, filterTerm, valueKey) => {
      return value?.length > 0 && this.filter(values, filterTerm, nameKey).every(v => {
        return value?.find(val => {
          return v[valueKey] === val;
        }) != null;
      });
    }, values, value, filterTerm, valueKey);
  }
  get cValues() {
    const {
      values = [],
      valueKey,
      nameKey
    } = this.props;
    let {
      value
    } = this.props;
    value = value || [];
    return value?.map(v => {
      const val = values.find(item => item[valueKey] === v);
      if (val) {
        return val;
      }
      return {
        [nameKey]: v,
        [valueKey]: v
      };
    }) || [];
  }
  get renderValues() {
    const {
      values = [],
      valueNameAccessor,
      value,
      valueKey,
      nameKey
    } = this.props;
    return this.cache.calc('renderValues', (values, value, valueKey, nameKey) => {
      const valuesByValue = values?.reduce((prev, curr) => {
        return {
          ...prev,
          [`${curr[valueKey]}`]: curr
        };
      }, {}) || {};
      const createdValues = value?.reduce((prev, curr) => {
        if (!valuesByValue[curr]) {
          prev.push({
            [nameKey]: valueNameAccessor ? valueNameAccessor(curr, values) : curr,
            [valueKey]: curr
          });
        }
        return prev;
      }, []) || [];
      return [...values, ...createdValues];
    }, values, value, valueKey, nameKey);
  }
  get popupProps() {
    const theme = this.context;
    const {
      selectionType = 'text',
      toggleVariant = 'multiSelector|selector',
      toggleAttributes,
      title,
      icon,
      values = [],
      className,
      popupVariant = 'multiSelector|selector',
      constraints,
      emptyLabel = 'None',
      disabled,
      renderPopupTo
    } = theme.props('MultiSelector', this.props);
    const {
      opened
    } = this.state;
    const props = {
      className,
      setPopupMinWidth: true,
      variant: popupVariant || 'multiSelector|selector',
      opened,
      onChange: this.onTogglePopup,
      constraints,
      renderTo: renderPopupTo
    };
    if (selectionType === 'text') {
      const cValues = this.cValues;
      const name = this.valueNames.length > 0 ? this.valueNames.reduce((prev, curr, index) => {
        if (index > 2) {
          return prev;
        }
        if (index === 2) {
          const otherCount = cValues.length - 2;
          return `${prev} and ${otherCount} other${otherCount === 1 ? '' : 's'}`;
        }
        return `${prev}${index === this.valueNames.length - 1 ? curr : `${curr}, `}`;
      }, '') : emptyLabel;
      let cTitle = title || name;
      if (typeof cTitle === 'function') {
        cTitle = cTitle(cValues, values);
      }
      let cIcon = icon;
      if (typeof cIcon === 'function') {
        cIcon = cIcon(cValues, values);
      }
      props.toggleVariant = toggleVariant || 'multiSelector|selector';
      props.toggleAttributes = {
        iconPosition: 'end',
        disabled,
        ...toggleAttributes
      };
      props.title = cTitle;
      props.icon = cIcon;
    }
    if (selectionType === 'tags') {
      props.trigger = this.renderTrigger;
    }
    return props;
  }
  onTogglePopup = ({
    isOpened
  }) => {
    this.setState({
      opened: isOpened
    });
  };
  componentDidUpdate(prevProps) {
    const {
      values,
      value,
      valueKey
    } = this.props;
    const {
      values: prevValues,
      value: prevValue
    } = prevProps;
    if (values !== prevValues) {
      this.valuesMap = values?.reduce((prev, curr) => {
        prev[`${curr[valueKey]}`] = curr;
        return prev;
      }, {}) || {};
    }
    if (value !== prevValue) {
      this.valueNames = this.getValueNames(value);
      this.forceUpdate();
    }
  }
  getValueNames(value) {
    const {
      valueNameAccessor,
      nameKey,
      values
    } = this.props;
    let valueNames = [];
    if (value != null) {
      if (valueNameAccessor) {
        let hasPromise = false;
        const exec = [];
        value.forEach(v => {
          const r = valueNameAccessor(v, values);
          if (r?.then) {
            hasPromise = true;
            valueNames.push(v);
            exec.push(r.catch(err => {
              console.error(err);
              return v;
            }));
          } else {
            valueNames.push(r);
            exec.push(r);
          }
          return r;
        });
        if (hasPromise) {
          Promise.all(exec).then(res => {
            this.valueNames = res;
            this.forceUpdate();
          });
        }
      } else {
        valueNames = value.map(v => {
          return this.valuesMap?.[v]?.[nameKey] || v;
        });
      }
    }
    return valueNames;
  }

  /**
   * @method onSelect Fire when the user selects an item. If given an onChange function, fire that as a side effect passing through eventData.
   * @param {object} evt
   */
  onSelect = evt => {
    const {
      onChange,
      eventData,
      value,
      clearSearchOnSelect
    } = this.props;
    const {
      data: {
        checked
      }
    } = evt;
    let nValue = [...(value || [])];
    if (checked) {
      nValue = nValue.filter(v => {
        return v !== evt.value;
      });
    } else {
      nValue.push(evt.value);
    }
    if (onChange) {
      onChange({
        value: nValue,
        data: eventData
      });
    }
    setTimeout(() => {
      this.inputRef.current?.focus();
    }, 1);
    if (clearSearchOnSelect) {
      this.setState({
        filterTerm: null
      });
    }
  };
  onSelectAll = ({
    value
  }) => {
    const {
      values,
      value: pv,
      nameKey,
      valueKey,
      eventData,
      onChange,
      clearSearchOnSelect
    } = this.props;
    const prevValue = pv !== undefined && pv !== null ? Array.isArray(pv) ? pv : [pv] : [];
    let filteredValues = values;
    const {
      filterTerm
    } = this.state;
    let allSelected = this.allSelected;
    if (value === 'filterAllSelected') {
      filteredValues = this.filter(filteredValues, filterTerm, nameKey);
      allSelected = this.allFilteredSelected;
    }
    if (!allSelected) {
      onChange?.({
        value: [...prevValue, ...filteredValues.map(v => v[valueKey]).filter(v => !prevValue.includes(v))],
        data: eventData
      });
    } else if (value === 'filterAllSelected') {
      onChange?.({
        value: prevValue.filter(v => {
          return !filteredValues.find(fv => fv[valueKey] === v);
        }),
        data: eventData
      });
    } else {
      onChange?.({
        value: [],
        data: eventData
      });
    }
    if (clearSearchOnSelect) {
      this.setState({
        filterTerm: null
      });
    }
  };
  onAdd = evt => {
    const {
      onChange,
      eventData,
      value,
      allowCreate,
      clearSearchOnSelect
    } = this.props;
    if (!allowCreate) {
      return;
    }
    const {
      data: {
        checked
      }
    } = evt;
    let nValue = [...(value || [])];
    if (checked) {
      nValue = nValue.filter(v => {
        return v !== evt.value;
      });
    } else if (nValue.indexOf(evt.value) === -1) {
      nValue.push(evt.value);
    }
    if (onChange) {
      onChange({
        value: nValue,
        data: eventData
      });
    }
    if (clearSearchOnSelect) {
      this.setState({
        filterTerm: null
      });
    }
    setTimeout(() => {
      this.inputRef.current?.focus();
    }, 1);
  };

  /**
   * @method onChangeFilter Updates the filterTerm when the user changes it.
   * @param {object} evt The event containing the input filter term value
   */
  onChangeFilter = evt => {
    const {
      onChangeFilter
    } = this.props;
    const filterTerm = evt.value;
    return this.setState({
      filterTerm
    }, typeof onChangeFilter === 'function' ? () => {
      onChangeFilter(evt);
    } : undefined);
  };
  onTagKeyPress = evt => {
    const theme = this.context;
    const {
      filterTerm
    } = this.state;
    const {
      value,
      selectionType = 'text',
      stopPropagation = true,
      preventDefault = true
    } = theme.props('MultiSelector', this.props);
    if (stopPropagation) {
      evt.stopPropagation();
    }
    if (evt.key === 'Enter' && `${filterTerm != null ? filterTerm : ''}`.length > 0) {
      if (preventDefault) {
        evt.preventDefault();
      }
      this.onAdd({
        value: filterTerm,
        data: {
          checked: false
        }
      });
    }
    if (selectionType === 'tags' && evt.key === 'Backspace') {
      if (`${filterTerm != null ? filterTerm : ''}`?.length === 0) {
        if (value && value[value.length - 1] != null) {
          this.onSelect({
            value: value[value.length - 1],
            data: {
              checked: true
            }
          });
        }
      }
    }
  };

  /**
   * @method filter Filters the values by some user input filterTerm
   * @param {Array.<object>} values An array of `{ nameKey : name, valueKey : value }` objects among which to select.
   * @param {string} filterTerm The term for which to filter the `values`.
   * @param {string} nameKey The attribute of an element of the `values` array on which to filter using the `filterTerm`.
   * @returns {array} `values` filtered using the `filterTerm` on each element's `value[nameKey]` attribute.
   */
  filter = (values, filterTerm, nameKey) => {
    return values.filter(value => {
      const isScalar = typeof value[nameKey] === 'string' || typeof value[nameKey] === 'number';
      const term = `${filterTerm != null ? filterTerm : ''}`?.toLowerCase();
      return isScalar && `${value[nameKey]}`.toLowerCase().includes(term);
    });
  };
  renderTrigger = ({
    ref,
    onToggle,
    setOpened
  }) => {
    const theme = this.context;
    const {
      filterTerm
    } = this.state;
    const {
      autoFocus = true,
      nameKey,
      valueKey,
      className,
      tagClassName,
      removeButtonVariant,
      variant,
      icon,
      iconOpts,
      imageOpts,
      tagInputVariant,
      type,
      float,
      inputProps,
      'data-error': dataError,
      error: pError
    } = theme.props('MultiSelector', this.props);
    const cValues = this.cValues;
    const cn = theme.classes('MultiSelector', 'multiSelector', variant, className, S.tagContainer);
    const tcn = theme.subclasses('MultiSelector', 'tag', tagClassName, S.tag);
    const sIcon = Icon.create(icon, imageOpts, iconOpts, theme);
    let err;
    if (pError != null) {
      err = !!pError;
    } else if (dataError != null) {
      err = dataError;
    }
    const InputComponent = type === 'number' || float ? NumericInput : TextInput;
    return /*#__PURE__*/_jsxs("div", {
      ref: ref,
      className: cn,
      onClick: () => {
        setOpened(true);
        this.inputRef.current?.focus();
      },
      children: [/*#__PURE__*/_jsxs("div", {
        className: S.tagEditorContainer,
        children: [cValues.map((v, index) => {
          const val = v[valueKey];
          const isScalar = typeof val === 'string' || typeof val === 'number';
          return /*#__PURE__*/_jsx(Tag, {
            className: tcn,
            variant: removeButtonVariant,
            onRemove: () => {
              this.onSelect({
                value: v[valueKey],
                data: {
                  checked: true
                }
              });
            },
            item: v,
            name: this.valueNames[index] || v[nameKey]
          }, `Item-${isScalar ? `${val}` : v[nameKey]}`);
        }), /*#__PURE__*/_jsx(InputComponent, {
          ...inputProps,
          error: err,
          autoFocus: autoFocus,
          float: float || undefined,
          variant: tagInputVariant,
          domRef: this.inputRef,
          onKeyDown: this.onTagKeyPress,
          className: S.tagInput,
          value: filterTerm,
          size: Math.max(filterTerm?.length || 0, 20),
          onChange: this.onChangeFilter,
          changeOnReturn: false,
          alwaysFireOnReturn: false
        })]
      }), sIcon]
    });
  };
  render() {
    const theme = this.context;
    const {
      filterTerm
    } = this.state;
    const {
      autoFocus = true,
      values,
      nameKey,
      valueKey,
      renderer,
      showFilter,
      checkStyle = 'check',
      checkVariant = 'default',
      stickyFilter,
      filterVariant,
      filterOptions,
      selectionType = 'text',
      allowCreate,
      showSelectAll,
      showFilterSelectAll,
      type,
      float,
      createPrefix,
      disabled,
      'data-error': dataError,
      error: pError,
      inputProps
    } = theme.props('MultiSelector', this.props);
    const {
      t
    } = Theme.I18N.ns('MultiSelector');
    let {
      value
    } = this.props;
    value = value || [];
    let renderValues = this.renderValues;
    if (showFilter || selectionType === 'tags') {
      renderValues = this.filter(renderValues, filterTerm, nameKey);
    }
    const allSelected = this.allSelected;
    const allFilteredSelected = this.allFilteredSelected;
    const InputComponent = type === 'number' || float ? NumericInput : TextInput;
    let err = false;
    if (pError != null) {
      err = !!pError;
    } else if (dataError != null) {
      err = dataError;
    }
    return /*#__PURE__*/_jsxs(Popup, {
      ...this.popupProps,
      children: [showFilter && selectionType === 'text' && /*#__PURE__*/_jsx(Popup.Header, {
        sticky: stickyFilter,
        children: /*#__PURE__*/_jsx(InputComponent, {
          ...inputProps,
          error: err,
          float: float || undefined,
          autoFocus: autoFocus,
          variant: filterVariant,
          className: S.filterInput,
          onChange: this.onChangeFilter,
          onKeyDown: this.onTagKeyPress,
          value: filterTerm,
          changeOnReturn: false,
          alwaysFireOnReturn: false,
          name: "selectorFilter",
          hint: t('placeholder', filterOptions.placeholder)
        })
      }), showSelectAll && values?.length > 0 && /*#__PURE__*/_jsx(Popup.Item, {
        disabled: disabled,
        close: false,
        checkStyle: checkStyle,
        checkVariant: checkVariant,
        checked: allSelected,
        onClick: this.onSelectAll,
        value: "allSelected",
        children: allSelected ? 'Deselect All' : 'Select All'
      }), showFilterSelectAll && `${filterTerm != null ? filterTerm : ''}`?.length > 0 && values?.length > 0 && /*#__PURE__*/_jsx(Popup.Item, {
        disabled: disabled,
        close: false,
        checkStyle: checkStyle,
        checkVariant: checkVariant,
        checked: allFilteredSelected,
        onClick: this.onSelectAll,
        value: "filterAllSelected",
        children: allSelected ? t('filterDeselectAll', {
          filterTerm,
          count: values.length
        }) : t('filterSelectAll', {
          filterTerm,
          count: values.length
        })
      }), allowCreate && `${filterTerm != null ? filterTerm : ''}`?.length > 0 && /*#__PURE__*/_jsxs(Popup.Item, {
        disabled: disabled,
        close: false,
        eventData: {
          checked: false
        },
        value: filterTerm,
        onClick: this.onAdd,
        children: [createPrefix ? `${createPrefix} ` : '', "\"", filterTerm, "\""]
      }), renderValues.map(val => {
        const checked = value.find(v => {
          return v === val[valueKey];
        });
        const name = val[nameKey];
        const v = val[valueKey];
        const isScalar = typeof v === 'string' || typeof v === 'number';
        const view = renderer == null ? name : renderer(val, name, checked);
        return /*#__PURE__*/_jsx(Popup.Item, {
          disabled: disabled,
          close: false,
          eventData: {
            checked,
            originalValue: val
          },
          checkStyle: checkStyle,
          checkVariant: checkVariant,
          checked: checked,
          value: v,
          onClick: this.onSelect,
          children: view
        }, `Item-${isScalar ? `${v}` : name}`);
      })]
    });
  }
}
function Tag({
  className,
  onRemove,
  removeButtonVariant = 'multiSelector|link',
  name,
  removeIcon = 'fa fa-close fa-fw'
}) {
  return /*#__PURE__*/_jsxs("div", {
    className: className,
    children: [/*#__PURE__*/_jsx("span", {
      children: name
    }), /*#__PURE__*/_jsx(Button, {
      stopPropagation: true,
      variant: removeButtonVariant,
      icon: removeIcon,
      onClick: onRemove
    })]
  });
}